import { type ReactNode } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ToastContainer, Button } from '@/components/ui'
import { BetaTag } from '@/components/ui/BetaTag'
import { SupportSessionBanner } from '@/components/layout/SupportSessionBanner'
import { OnboardingStepBar } from './OnboardingStepBar'

interface OnboardingLayoutProps {
  children: ReactNode
  /** Current wizard step (1-based). Required when showStepBar is true. */
  currentStep?: number
  /**
   * Whether to render the labelled desktop step-bar under the top bar.
   * Defaults to true. Pass false for surfaces with no step context (the
   * landing / mode-chooser view, the import flow — which has its own 5-step
   * indicator — and ExplorerModePage).
   */
  showStepBar?: boolean
  /**
   * Callback for the "Exit wizard" button in the top bar. When provided,
   * renders as a <button> that calls this handler (navigates to "/" without
   * writing onboarding_step). When omitted, falls back to a <Link to="/">
   * for backward compatibility.
   */
  onSkip?: () => void
  /**
   * Forwarded to the labelled desktop step bar so its completed segments can
   * navigate backward, same as the footer's minimal bar. Without it desktop
   * has no click-to-go-back at all: before EP-114 the minimal bar rendered at
   * every breakpoint and carried this, and moving desktop to the labelled bar
   * dropped it. Omit on surfaces with no step context.
   */
  onStepClick?: (step: number) => void
  /**
   * EP-158 AC-2: the furthest step reached in this wizard session, forwarded
   * to the labelled desktop step bar so a segment past the current step
   * (already visited once) is also clickable, not just completed/earlier
   * ones. Omit on surfaces with no step context, or to keep the bar's
   * original back-only behaviour (the import flow's own step indicator does
   * not use this layout at all).
   */
  reachedStep?: number
  /**
   * @deprecated No longer used — Resume Later link was removed (Q4) and the
   * mobile drawer it lived in was removed (EP-80 #11). Kept as an optional
   * no-op prop to avoid breaking callers still passing it during migration.
   */
  onResumeLater?: () => void
}

// `currentStep` / `showStepBar` are load-bearing (EP-114 T003, Figma
// 2026-08): the labelled 8-segment step bar renders here, under the top bar,
// on md+ viewports only. Mobile keeps the unlabelled minimal bar in the
// wizard footer (OnboardingBottomBar) exactly as it did before this change —
// this is why the desktop call site below MUST stay wrapped in `hidden
// md:block`. OnboardingStepBar's `showLabels` (default) branch renders its
// OWN internal `md:hidden` mobile fallback ("Step N of 8" + dots) alongside
// the desktop nav; without the outer `hidden` wrapper that fallback would
// also render, unguarded, at the top of the page on mobile — doubling up
// with the footer's minimal bar.
export function OnboardingLayout({ children, currentStep, showStepBar = true, onSkip, onStepClick, reachedStep }: OnboardingLayoutProps) {
  const { t } = useTranslation('onboarding')

  return (
    <div className="min-h-screen flex flex-col bg-base-100" data-testid="onboarding-layout">
      {/* Top bar — brand lockup (mark + wordmark, left) + Exit wizard (right).
          EP-80 #11: the mobile hamburger + empty drawer were removed (the
          wizard has no in-bar navigation, so the menu icon was dead UI).
          EP-80 #10: the wordmark is now in-flow + truncating (it used to be
          absolutely centered, overlapping the exit button on narrow phones);
          the exit button never shrinks/wraps, so it stays visible + tappable
          on every viewport.
          EP-114 T004 (S1-4 / Q-8): desktop navbar padding grows to p-10 and
          the wordmark to 22px; mobile keeps a lighter p-4 outer pad with the
          inner row itself carrying py-3 for vertical rhythm, wordmark 20px.
          A 32px brand mark (/icon-clean.svg) sits left of the wordmark at
          EVERY breakpoint, reusing Sidebar.tsx's exact lockup composition
          (Q-8) — not the Figma's pasted screenshot asset, which is not
          shippable. */}
      {/* Sticky so the bar stays pinned as the content scrolls (the native
          WKWebView scrolls the whole page). pt honours the top safe-area inset so
          the wordmark clears the status bar / dynamic island; max(...,0.75rem) is
          the mobile-Safari floor (Safari already reserves the status bar). The
          native Capacitor shell does NOT populate env(safe-area-inset-top) (it
          returns 0 while still rendering edge-to-edge), so `.native-shell` gets a
          real status-bar inset via index.css. The white bg fills the inset. */}
      {/* Height reverted to the pre-EP-114 geometry (owner, 2026-08-04). T004 had
          grown this to `p-4 md:p-10` plus a `py-3` inner row, taking the desktop
          header from ~45px to 113px. The padding is back to the original
          `px-4 pt-[safe-area] pb-3`; the brand lockup STAYS, so the row is now
          sized by the 32px mark rather than the old text-only wordmark. The
          Figma frame specifies a 120px navbar; that is knowingly not followed
          here. */}
      {/* EP-160 S3: a FlowRent support tab shows its banner on these
          full-screen pages too (the import flow is reachable in a session).
          One sticky block holds the banner and the bar, so the two stay
          pinned together; inside it neither sticky has any effect. */}
      <div className="sticky top-0 z-30">
        <SupportSessionBanner />
        <header
          className="sticky top-0 z-30 px-4 pt-[max(env(safe-area-inset-top),0.75rem)] pb-3 border-b border-base-200 bg-base-100"
          data-testid="onboarding-top-bar"
        >
          <div
            className="flex justify-between items-center gap-3"
            data-testid="onboarding-top-bar-row"
          >
            {/* Brand lockup (left): 32px mark + wordmark + BetaTag */}
            <div className="min-w-0 flex items-center gap-2">
              <img
                src="/icon-clean.svg"
                alt=""
                className="w-8 h-8 rounded-lg ring-1 ring-base-300 shrink-0"
              />
              <span
                className="min-w-0 truncate font-bold text-xl md:text-[22px] text-primary select-none inline-flex items-center gap-2"
                data-testid="onboarding-wordmark"
              >
                FlowRent
                <BetaTag />
              </span>
            </div>

            {/* Exit wizard button (right) — visible on all viewports.
                EP-114 T004: neutral-outline treatment (T001's
                'fd-outline-neutral' variant — fd-blue-active border /
                fd-grey-darker text tokens), replacing the plain 'outline'
                variant. */}
            {onSkip ? (
              <Button
                variant="fd-outline-neutral"
                size="sm"
                className="flex-shrink-0 whitespace-nowrap"
                data-testid="skip-btn"
                onClick={onSkip}
              >
                {t('layout.exit_wizard')}
              </Button>
            ) : (
              <Button
                as={Link}
                to="/"
                variant="fd-outline-neutral"
                size="sm"
                className="flex-shrink-0 whitespace-nowrap"
                data-testid="skip-btn"
              >
                {t('layout.exit_wizard')}
              </Button>
            )}
          </div>
        </header>
      </div>

      {/* Labelled desktop step bar, under the top bar (Figma 2026-08 / EP-114 T003).
          `hidden md:block` gates the WHOLE call site — see the comment above the
          component for why: OnboardingStepBar's labelled variant renders its own
          `md:hidden` mobile fallback internally, and that must never render
          unguarded here (mobile keeps only the footer's minimal bar). */}
      {showStepBar && currentStep !== undefined && (
        <div
          className="hidden md:block border-b border-base-200 px-6 py-2"
          data-testid="onboarding-step-bar-desktop-wrapper"
        >
          <OnboardingStepBar currentStep={currentStep} showLabels onStepClick={onStepClick} reachedStep={reachedStep} />
        </div>
      )}

      {/* Page content — extra bottom padding clears the fixed footer (stepper +
          buttons) so the last field isn't hidden behind it. */}
      <main className="flex-1 overflow-auto pb-28" data-testid="onboarding-content">
        {children}
      </main>

      {/* Toast notifications — the store is global but the renderer is
          per-layout. MainLayout has its own ToastContainer; the wizard
          needs its own copy here, otherwise validation toasts (e.g.
          CustomerFields' "identifier required") fire silently. */}
      <ToastContainer />
    </div>
  )
}
